import { useCallback, useEffect, useMemo, useState } from 'react';
import type { CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import { Button } from '@actual-app/components/button';
import { styles } from '@actual-app/components/styles';
import { View } from '@actual-app/components/view';
import { keyframes } from '@emotion/css';

import {
  comprobarPlan,
  OPCIONES_QUE_ESCRIBEN,
} from '#components/mobile/plan/cerrojoPlan';
import { useAvisarProblema } from '#components/mobile/problema/AvisarProblema';
import { useCerrarConAtras } from '#components/mobile/ui/atras';
import { Icono } from '#components/mobile/ui/Icono';
import { color, movimiento, radio, sombra } from '#components/mobile/ui/tokens';
import { useNavigate } from '#hooks/useNavigate';
import { useEstadoSiHay } from '#redux';
import { registrarUso } from '#uso';

import {
  esPaginaMas,
  ID_AVISAR_PROBLEMA,
  opcionAnadirMovimiento,
  ordenarOpciones,
} from './opciones';
import type { OpcionPantalla } from './opciones';
import { useOpcionesRegistradas } from './OpcionesPantalla';

const aparece = keyframes({ from: { opacity: 0 }, to: { opacity: 1 } });
const sube = keyframes({
  from: { opacity: 0, transform: 'translateY(12px) scale(.94)' },
  to: { opacity: 1, transform: 'none' },
});

/**
 * Rosa de los vientos simplificada (H02), dibujada aquí: anillo y aguja en
 * rombo. La punta norte va en el tono de acento de la brújula y la sur en el
 * color del icono, atenuado (neutro). El anillo lleva el trazo de 2 px de los
 * iconos de la barra. Antes se usaba la «R» del logo, que parecía «®»; la «R»
 * queda solo para la marca.
 */
export function IconoBrujula({ size = 22 }: { size?: number }) {
  return (
    <svg
      data-icono="brujula"
      aria-hidden
      role="presentation"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ flexShrink: 0, display: 'block' }}
    >
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth={2} />
      {/* Sur: neutro (el color del icono, atenuado). */}
      <path
        data-punta="sur"
        d="M12 20.2 15.3 12H8.7z"
        fill="currentColor"
        fillOpacity={0.5}
        stroke="currentColor"
        strokeOpacity={0.5}
        strokeWidth={1.2}
      />
      {/* Norte: tono de acento. */}
      <path
        data-punta="norte"
        d="M12 3.8 8.7 12h6.6z"
        fill={color.brujulaNorte}
        stroke={color.brujulaNorte}
        strokeWidth={1.2}
      />
      <circle cx="12" cy="12" r="1.1" fill="currentColor" />
    </svg>
  );
}

type MenuOpcionesProps = {
  /** Posición del botón redondo (suelto, a la derecha de la barra). */
  estiloBoton?: CSSProperties;
  /** Distancia al borde inferior: las píldoras salen justo encima. */
  desdeAbajo: string;
  /** Cambia con la ruta: al navegar se cierra. */
  claveRuta?: string;
  /**
   * Abierto o cerrado desde fuera (la barra lo necesita para atenuarse y
   * cerrarlo al tocarla, B5). Sin él, la brújula lleva su propio estado.
   */
  abierto?: boolean;
  onCambiarAbierto?: (abierto: boolean) => void;
};

/** Latón de Gavi (ojo de buey) para la píldora de avisar de un problema. */
const LATON_PILDORA = 'rgba(201, 151, 59, 0.9)';

/** Diámetro del botón de la brújula. */
export const DIAMETRO_BRUJULA = 52;

/**
 * Botón «Opciones» (brújula redonda y verde, suelta a la derecha de la barra
 * para que no parezca una pestaña) y sus píldoras: las opciones de la
 * pantalla actual, que salen hacia arriba alineadas a la derecha sobre un
 * velo que cierra al tocarlo (o con «atrás» del móvil). «Más» va siempre la
 * última, en gris y pegada al botón (salvo en la propia página «Más»).
 */
export function MenuOpciones({
  estiloBoton,
  desdeAbajo,
  claveRuta,
  abierto: abiertoFuera,
  onCambiarAbierto,
}: MenuOpcionesProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [abiertoPropio, setAbiertoPropio] = useState(false);
  const controlado = abiertoFuera !== undefined;
  const abierto = controlado ? abiertoFuera : abiertoPropio;
  const setAbierto = useCallback(
    (nuevo: boolean) => {
      if (!controlado) {
        setAbiertoPropio(nuevo);
      }
      onCambiarAbierto?.(nuevo);
    },
    [controlado, onCambiarAbierto],
  );
  const registradas = useOpcionesRegistradas();
  // «Avisar de un problema aquí»: solo con cuenta en el servidor (llega al
  // Puente de mando de quien lleva Rumbo).
  const userData = useEstadoSiHay()?.user.data;
  const conCuenta = Boolean(userData?.userId) && !userData?.offline;
  const avisar = useAvisarProblema();

  const opciones = useMemo(
    () =>
      ordenarOpciones(
        registradas,
        // «Más»: se llama como la página que abre. El id sigue siendo
        // `ajustes` para no romper los datos de uso ya guardados.
        {
          id: 'ajustes',
          tipo: 'ajustes',
          texto: t('More'),
          icono: 'more',
          onPress: () => void navigate('/mas'),
          oculta: esPaginaMas(claveRuta),
        },
        // En todas las pantallas; la cuenta o la categoría la cambian por la
        // suya (mismo id) para que el movimiento nuevo las lleve puestas.
        opcionAnadirMovimiento(
          t('Add transaction'),
          () => void navigate('/transactions/new'),
        ),
        {
          id: ID_AVISAR_PROBLEMA,
          tipo: 'problema',
          texto: t('Report a problem here'),
          icono: 'salvavidas',
          onPress: avisar.abrir,
          oculta: !conCuenta,
        },
      ),
    [registradas, t, navigate, claveRuta, conCuenta, avisar.abrir],
  );

  // Al cambiar de pantalla, las opciones de la anterior ya no valen.
  useEffect(() => {
    setAbierto(false);
    // Solo al cambiar de ruta, no cuando cambia quién guarda el estado.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [claveRuta]);

  useEffect(() => {
    if (!abierto) {
      return;
    }
    const alPulsar = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setAbierto(false);
      }
    };
    window.addEventListener('keydown', alPulsar);
    return () => window.removeEventListener('keydown', alPulsar);
  }, [abierto, setAbierto]);

  // «Atrás» de Android cierra la brújula antes que la pantalla.
  const cerrarYLuego = useCerrarConAtras(abierto, () => setAbierto(false));

  const elegir = (o: OpcionPantalla) => {
    registrarUso('brujula_opcion', { opcion: o.id, tipo: o.tipo });
    // Puerta de planes: en «solo ver», lo que cambia datos abre la hoja.
    if (OPCIONES_QUE_ESCRIBEN.has(o.id) && !comprobarPlan()) {
      setAbierto(false);
      return;
    }
    // La opción se ejecuta con el historial ya limpio de la entrada de la
    // brújula: si navega, «atrás» vuelve a esta pantalla a la primera.
    cerrarYLuego(o.onPress);
  };

  return (
    <>
      <Button
        variant="bare"
        aria-expanded={abierto}
        aria-haspopup="true"
        aria-label={t('Options')}
        data-testid="brujula-opciones"
        onPress={() => {
          if (!abierto) {
            // Datos de uso: qué opciones se ofrecían (solo sus ids).
            registrarUso('brujula_abrir', {
              opciones: opciones.map(o => o.id),
            });
          }
          setAbierto(!abierto);
        }}
        style={({ isPressed }) => ({
          ...styles.noTapHighlight,
          width: DIAMETRO_BRUJULA,
          height: DIAMETRO_BRUJULA,
          minWidth: DIAMETRO_BRUJULA,
          padding: 0,
          borderRadius: '50%',
          // Colores por tema (B18): en oscuro, capa con anillo de acento;
          // abierta, además un halo, para que se note el estado sin el verde.
          backgroundColor: color.brujulaBg,
          color: color.brujulaInk,
          boxShadow: `inset 0 0 0 2px ${color.brujulaAnillo}, ${abierto ? `0 0 0 4px ${color.brujulaHalo}, ` : ''}${sombra.flotante}`,
          zIndex: 102,
          transform: `${isPressed ? 'scale(0.94) ' : ''}rotate(${abierto ? -35 : 0}deg)`,
          transition: `transform ${movimiento.hoja}ms ${movimiento.muelle}`,
          ...estiloBoton,
        })}
      >
        <IconoBrujula size={32} />
      </Button>
      {avisar.elemento}
      {abierto &&
        createPortal(
          <>
            <View
              data-testid="velo-opciones"
              aria-hidden
              onClick={() => setAbierto(false)}
              style={{
                position: 'fixed',
                inset: 0,
                zIndex: 99,
                backgroundColor: color.velo,
                animation: `${aparece} ${movimiento.fondo}ms ease-out`,
              }}
            />
            <View
              role="group"
              aria-label={t('Options for this screen')}
              data-testid="opciones-pantalla"
              style={{
                position: 'fixed',
                right: 14,
                bottom: desdeAbajo,
                zIndex: 101,
                alignItems: 'flex-end',
                gap: 10,
                maxHeight: `calc(100vh - ${desdeAbajo} - 24px)`,
                overflowY: 'auto',
                padding: '4px 2px',
              }}
            >
              {opciones.map((o, i) => (
                <Pildora
                  key={o.id}
                  opcion={o}
                  indice={opciones.length - 1 - i}
                  onPress={() => elegir(o)}
                />
              ))}
            </View>
            <style>
              {
                '@media (prefers-reduced-motion: reduce){[data-testid="opciones-pantalla"] *,[data-testid="velo-opciones"]{animation:none!important}}'
              }
            </style>
          </>,
          document.body,
        )}
    </>
  );
}

function Pildora({
  opcion,
  indice,
  onPress,
}: {
  opcion: OpcionPantalla;
  /** Distancia a la barra: las de abajo salen antes. */
  indice: number;
  onPress: () => void;
}) {
  const ajustes = opcion.tipo === 'ajustes';
  // «Avisar de un problema»: borde discontinuo de latón, para que se vea
  // distinta de las acciones de la pantalla.
  const problema = opcion.tipo === 'problema';
  return (
    <Button
      variant="bare"
      data-testid={`opcion-${opcion.id}`}
      isDisabled={opcion.desactivada}
      onPress={onPress}
      style={({ isPressed }) => ({
        ...styles.noTapHighlight,
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: 10,
        minHeight: 46,
        padding: '6px 15px 6px 6px', // 1 px menos: lo pone el borde
        borderRadius: radio.pildora,
        backgroundColor: color.pildoraBg,
        border: problema
          ? `1.5px dashed ${LATON_PILDORA}`
          : `1px solid ${color.pildoraBorde}`,
        color: ajustes ? color.fg2 : color.fg,
        boxShadow: sombra.flotante,
        fontSize: 14,
        fontWeight: 700,
        whiteSpace: 'nowrap',
        flexShrink: 0,
        opacity: opcion.desactivada ? 0.5 : 1,
        transform: isPressed ? 'scale(0.96)' : undefined,
        animation: `${sube} ${movimiento.hoja}ms ${movimiento.muelle} both`,
        animationDelay: `${indice * 28}ms`,
      })}
    >
      <span
        aria-hidden
        style={{
          width: 32,
          height: 32,
          borderRadius: '50%',
          flexShrink: 0,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: ajustes
            ? color.pildoraIconoGris
            : problema
              ? 'rgba(201, 151, 59, 0.18)'
              : color.accentSoftFuerte,
          color: ajustes ? color.fg3 : problema ? '#8a6420' : color.accent,
        }}
      >
        <Icono nombre={opcion.icono} size={17} />
      </span>
      {opcion.texto}
    </Button>
  );
}
